import { useLatest } from 'ahooks';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IImageLayerProps, ILayerProps, IPuzzleLayerProps, IRenderLayerExtraProps } from '@/version-types';
import { CoreAPI, getBehavior, ICore, useIdentity, useVersion } from './core';
import { OperateAPI } from './operate';
import { useActiveOriginIndex, useIsLongImageMode, useStageList } from './stage/hooks';
import { getStageInstance } from './stage/instance';
import { ICustomLayer } from './stage/type';

/**
 * 渲染舞台
 */
export const RenderStage: React.FC<{ isShowStageMask?: boolean }> = React.memo(({ isShowStageMask = true }) => {
    const isLongImageMode = useIsLongImageMode();
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const loadingLayerIdsRef = useRef<Set<string>>(new Set());
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    function startLoading() {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            loadingLayerIdsRef.current.forEach((data) => {
                const [layerId, extraInfo] = data.split('::');
                coreApi.setLoading(layerId, extraInfo);
            });
            loadingLayerIdsRef.current.clear();
        }, 500);
    }
    const extraProps: IRenderLayerExtraProps = {
        identity,
        isNavigator: false,
        enterLoading: (layerId: string, extraInfo: string = '') => {
            loadingLayerIdsRef.current.add(`${layerId}::${extraInfo}`);
            startLoading();
        },
        exitLoading: (layerId: string, extraInfo: string = '') => {
            loadingLayerIdsRef.current.delete(`${layerId}::${extraInfo}`);
            coreApi.removeLoading(layerId, extraInfo);
        },
        isShowStageMask,
    };

    return isLongImageMode ? <VarietyStageNode extraProps={extraProps} /> : <SingleStageNode extraProps={extraProps} />;
});

/**
 * 渲染单个舞台
 */
const SingleStageNode: React.FC<{ extraProps: IRenderLayerExtraProps }> = React.memo(({ extraProps }) => {
    const activeOriginIndex = useActiveOriginIndex();
    const version = useVersion();
    return (
        <RenderStageView
            version={version}
            originIndex={activeOriginIndex}
            extraProps={extraProps}
            isLongImageMode={false}
        />
    );
});

/**
 * 渲染长图舞台
 */
const VarietyStageNode: React.FC<{ extraProps: IRenderLayerExtraProps }> = React.memo(({ extraProps }) => {
    const version = useVersion();
    const stageList = useStageList();
    const coreApi = CoreAPI(extraProps.identity);
    const getMouseEvents = useCallback((originIndex: number) => {
        return {
            onMouseEnter: () => {
                coreApi.events.emitMouseEnter(originIndex);
            },
            onMouseLeave: () => {
                coreApi.events.emitMouseLeave(originIndex);
            },
        };
    }, []);
    return (
        <>
            {stageList.map((originIndex) => {
                return (
                    <RenderStageView
                        key={originIndex}
                        version={version}
                        extraProps={extraProps}
                        originIndex={originIndex}
                        isLongImageMode={true}
                        mouseEvents={getMouseEvents}
                    />
                );
            })}
        </>
    );
});

/**
 * 渲染一个指定舞台
 */
export const RenderStageView: React.FC<{
    version?: number;
    originIndex: number;
    isLongImageMode: boolean;
    /** 长图舞台鼠标事件 */
    mouseEvents?: (originIndex: number) => {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
    extraProps: IRenderLayerExtraProps;
}> = React.memo(({ version, originIndex, isLongImageMode, mouseEvents, extraProps }) => {
    const coreApi = CoreAPI(extraProps.identity);
    const [viewIds, setViewIds] = useState<string[]>([]);
    const viewIdsRef = useLatest(viewIds);
    const preVersionRef = useRef(version);
    const [showMask, setShowMask] = useState(true);

    useEffect(() => {
        if (isLongImageMode) return;
        const handler = coreApi.events.onRerenderAll(() => {
            if (!viewIdsRef.current.length) {
                setViewIds([...coreApi.getRenderIds(originIndex)]);
            }
        });
        return () => coreApi.events.off(handler);
    }, [isLongImageMode, originIndex]);

    useEffect(() => {
        preVersionRef.current = version;
        setViewIds([...coreApi.getRenderIds(originIndex)]);
        const rerenderHandler = coreApi.events.onRerenderStage(originIndex, (originIndex) => {
            setViewIds([...coreApi.getRenderIds(originIndex)]);
        });
        const resizeHandler = coreApi.events.onStageResize((data, isRerenderAll) => {
            if (!isRerenderAll && data.find((v) => v.originIndex === originIndex)) {
                setViewIds([...coreApi.getRenderIds(originIndex)]);
            }
        });
        return () => {
            coreApi.events.off(rerenderHandler);
            coreApi.events.off(resizeHandler);
        };
    }, [originIndex, version]);
    // console.log('ot ============> RenderStageView', version, preVersionRef.current, viewIds);

    if (!viewIds.length || preVersionRef.current !== version) return null;
    const size = coreApi.getSize(originIndex);

    // ATTENTION: 多画布当前先不考虑有主图的场景
    const mainLayer = isLongImageMode ? null : coreApi.getMainLayer(originIndex);
    // HACK: 当前先临时支持舞台的遮罩（MASK）实现来满足主图的遮罩，这里后续应该是要定义舞台的遮罩定义，而不是通过主图
    const maskURL = (mainLayer?.data as IImageLayerProps)?.maskURL;

    return (
        <>
            <div
                data-index={coreApi.convertOriginToStage(originIndex)}
                className={classNames({
                    'render-single-stage-view': !extraProps.isNavigator && !isLongImageMode,
                    'render-variety-stage-view': !extraProps.isNavigator && isLongImageMode,
                })}
                style={{
                    width: size.width,
                    height: size.height,
                    WebkitMaskImage: showMask && maskURL && extraProps.isShowStageMask ? `url(${maskURL})` : undefined,
                    overflow: extraProps.isShowStageMask ? 'hidden' : 'visible',
                }}
                {...mouseEvents?.(originIndex)}
            >
                {!extraProps.isNavigator && <CustomLayer position="bottom" originIndex={originIndex} />}
                {viewIds.map((id) => {
                    const layer = coreApi.getLayer(id);
                    if (layer) {
                        return <LayerNode key={id} layer={layer} extraProps={extraProps} />;
                    }
                    return <React.Fragment key={id} />;
                })}
                {!extraProps.isNavigator && (
                    <CustomLayer position="top" originIndex={originIndex} setShowMask={setShowMask} />
                )}
            </div>
        </>
    );
});

/**
 * 自定义图层
 */
const CustomLayer: React.FC<{ position: string; originIndex: number; setShowMask?: (showMask: boolean) => void }> =
    React.memo(({ position, originIndex, setShowMask }) => {
        const identity = useIdentity();
        const stageInstance = getStageInstance(identity);
        const [customLayer, setCustomLayer] = useState<ICustomLayer[]>(stageInstance.customLayer.get());

        useEffect(() => {
            setCustomLayer(stageInstance.customLayer.get());
            return stageInstance.customLayer.onRender((data) => {
                setCustomLayer(data);
            });
        }, [originIndex]);

        const data = customLayer.filter((item) => item.originIndex === originIndex && item.position === position);

        useEffect(() => {
            if (data.length && data.find((item) => item.position === 'top')) {
                setShowMask?.(false);
            } else {
                setShowMask?.(true);
            }
        }, [data.length]);

        if (!data.length) return null;

        return (
            <>
                {data.map((item) => {
                    return <React.Fragment key={item.type}>{item.node}</React.Fragment>;
                })}
            </>
        );
    });

/**
 * 图层节点
 */
const LayerNode: React.FC<{ layer: ILayerProps; extraProps: IRenderLayerExtraProps }> = React.memo(
    ({ layer, extraProps }) => {
        const identity = useIdentity();
        const layerId = layer.id;
        const coreApi = CoreAPI(identity);
        const [_, setRerender] = useState(0);

        useEffect(() => {
            const handler = coreApi.events.onRerenderLayer(layerId, (id) => {
                setRerender((v) => v + 1);
            });
            return () => {
                coreApi.events.off(handler);
            };
        }, [layerId]);
        // console.log('layer => ', layerId, coreApi.getLayer(layerId));
        return <LayerView layer={coreApi.getLayer(layerId)} extraProps={extraProps} />;
    },
);

/**
 * 渲染图层
 */
export const LayerView: React.FC<{ layer: ILayerProps; extraProps: IRenderLayerExtraProps }> = React.memo(
    ({ layer, extraProps }) => {
        const coreApi = CoreAPI(extraProps.identity);
        if (!coreApi.checkIsNeedRender(layer.id) && !extraProps.isThumbnail) return null;
        if (coreApi.checkIsNeedHidden(layer.id) && !extraProps.ignoreHidden) return null;
        const isBasicOperating = OperateAPI(extraProps.identity).isBaseOperate();
        return <>{getLayerView(coreApi, layer, extraProps, isBasicOperating)}</>;
    },
);

export const PreviewStageView: React.FC<{ layers: ILayerProps[] }> = React.memo(({ layers }) => {
    const identity = useIdentity();
    return (
        <>
            {layers.map((item) => {
                return (
                    <LayerView
                        key={item.id}
                        layer={item}
                        extraProps={{
                            identity,
                            isNavigator: false,
                        }}
                    />
                );
            })}
        </>
    );
});

/**
 * 获取图层视图
 */
function getLayerView(
    coreApi: ICore,
    layer: ILayerProps,
    extraProps: IRenderLayerExtraProps,
    isBasicOperating: boolean,
) {
    const layerView: React.ReactNode[] = [];
    // 设置是否正在执行基础操作, 基础操作需要隐藏图片特效
    layer.__isBasicOperating = isBasicOperating;
    const { __animation } = layer;
    const animationProps = {
        ...(__animation?.enabled && !extraProps?.isNavigator
            ? {
                  opacity: 0,
                  animation: `${__animation?.name} ${__animation?.duration || 0.2}s ease-in ${__animation?.delay}s forwards`,
              }
            : {}),
    };
    const childrenViews: React.ReactNode[] = [];
    if (coreApi.checkIsFormalGroup(layer) && layer.children?.length && !coreApi.checkIsGroupExtension(layer)) {
        for (const child of layer.children) {
            if (child.hidden && !extraProps.ignoreHidden) continue;
            childrenViews.push(...getLayerView(coreApi, child, extraProps, isBasicOperating));
        }
    }

    layerView.push(
        <div key={layer.id} className="l-layer" style={{ ...animationProps }}>
            {childrenViews}
            {getBehavior(coreApi.identity, layer)?.render(layer, extraProps) || null}
        </div>,
    );

    return layerView;
}
